import { useCallback, useEffect, useRef, useState } from 'react';
import { City } from '../components/lookups/lookups.type';
import { isValidCityState } from '../components/lookups/lookups.util';
import { validateCityState } from '../components/lookups/lookups.api';

export type CityStateValidationStatus = 'unknown' | 'valid' | 'invalid' | 'checking';

/**
 * Validates a city/state pair against the local Cities lookup first; on a miss, `checkOnBlur()`
 * (wired to onBlur) falls back to a Google Geocode check via the backend. 'checking' means that
 * fallback is in flight.
 */
const useCityStateValidation = (cityOptions: City[], city?: string | null, state?: string | null) => {
  const [status, setStatus] = useState<CityStateValidationStatus>('unknown');
  const requestKeyRef = useRef<string>(''); // guards against a stale response landing late

  const trimmedCity = city?.trim() || '';
  const trimmedState = state?.trim() || '';

  useEffect(() => {
    const requestKey = `${trimmedCity.toLowerCase()}|${trimmedState.toLowerCase()}`;
    requestKeyRef.current = requestKey;

    if (!trimmedCity && !trimmedState) {
      setStatus('unknown');
      return;
    }
    if (!trimmedCity || !trimmedState) {
      setStatus('invalid');
      return;
    }
    if (cityOptions.length === 0) {
      setStatus('unknown');
      return;
    }
    setStatus(isValidCityState(cityOptions, trimmedCity, trimmedState) ? 'valid' : 'unknown');
  }, [cityOptions, trimmedCity, trimmedState]);

  // Runs the Google Geocode fallback for a pair the local lookup missed. No-ops if already
  // resolved or blank.
  const checkOnBlur = useCallback(() => {
    const requestKey = `${trimmedCity.toLowerCase()}|${trimmedState.toLowerCase()}`;
    if (!trimmedCity || !trimmedState) return;
    if (cityOptions.length === 0) return;
    if (isValidCityState(cityOptions, trimmedCity, trimmedState)) return;

    requestKeyRef.current = requestKey;
    setStatus('checking');
    validateCityState(trimmedCity, trimmedState)
      .then(result => {
        if (requestKeyRef.current !== requestKey) return;
        setStatus(result?.isValid ? 'valid' : 'invalid');
      })
      .catch(() => {
        if (requestKeyRef.current !== requestKey) return;
        setStatus('unknown');
      });
  }, [cityOptions, trimmedCity, trimmedState]);

  return { status, checkOnBlur };
};

export default useCityStateValidation;
